import { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  editableCellClass,
  editableCellCorrectClass,
  editableCellIncorrectClass,
  editableCellWarningClass,
} from "../TableQuestion.css";

const messages = defineMessages({
  cellCorrect: {
    id: "TableQuestion.cellCorrect",
    defaultMessage: "Correct",
    description:
      "Screen-reader status announced when a submitted table cell is correct",
  },
  cellIncorrect: {
    id: "TableQuestion.cellIncorrect",
    defaultMessage: "Incorrect",
    description:
      "Screen-reader status announced when a submitted table cell is incorrect",
  },
});

export interface UseEditableCellStateParams {
  value: string | number | null | undefined;
  showFeedback?: boolean;
  isCorrect?: boolean;
  feedback?: string;
  hint?: string;
  isPrediction?: boolean;
  isIncomplete?: boolean;
}

export interface EditableCellState {
  /** Whether the cell has a non-empty value */
  hasValue: boolean;
  /** CSS class for the cell container based on feedback state */
  cellClass: string;
  /** Input/Select state for styling: "default" | "success" | "error" | "warning" */
  state: "default" | "success" | "error" | "warning";
  /** Suffix icon to display: "check" | "cross" | undefined */
  suffixIcon: "check" | "cross" | undefined;
  /** Translated feedback message or undefined */
  translatedFeedback: string | undefined;
  /** Translated hint message or undefined */
  translatedHint: string | undefined;
  /** Whether feedback should be shown (not prediction mode, has feedback enabled, has value) */
  shouldShowFeedback: boolean;
  /**
   * Visually-hidden correctness word ("Correct"/"Incorrect") to associate with
   * the cell control via aria-describedby, or undefined when no feedback shows.
   * The check/cross icon and border colour are visual-only, so this is the sole
   * screen-reader signal of a cell's correctness after submit. Undefined for a
   * required-but-empty cell, which is incomplete rather than wrong.
   */
  feedbackStatus: string | undefined;
}

/**
 * Hook to extract common feedback/hint state logic for editable table cells.
 * Used by InputCell and DropdownCell to avoid code duplication.
 */
export const useEditableCell = ({
  value,
  showFeedback,
  isCorrect,
  feedback,
  hint,
  isPrediction,
  isIncomplete,
}: UseEditableCellStateParams): EditableCellState => {
  const intl = useIntl();

  return useMemo(() => {
    const hasValue = value !== null && value !== undefined && value !== "";

    const getCellClass = (): string => {
      if (isPrediction) {
        return editableCellClass;
      }
      if (isIncomplete && !hasValue) {
        return editableCellWarningClass;
      }
      if (showFeedback && hasValue) {
        return isCorrect
          ? editableCellCorrectClass
          : editableCellIncorrectClass;
      }
      return editableCellClass;
    };

    const getState = (): "default" | "success" | "error" | "warning" => {
      if (isPrediction) return "default";
      if (isIncomplete && !hasValue) return "warning";
      if (!showFeedback || !hasValue) return "default";
      return isCorrect ? "success" : "error";
    };

    const getSuffixIcon = (): "check" | "cross" | undefined => {
      if (isPrediction) return undefined;
      if (isIncomplete && !hasValue) return undefined;
      if (!showFeedback || !hasValue) return undefined;
      return isCorrect ? "check" : "cross";
    };

    const isRequiredEmpty = !!isIncomplete && !hasValue && !isPrediction;

    const translatedFeedback = isRequiredEmpty
      ? intl.formatMessage({
          id: "TableQuestion.fieldRequired",
          defaultMessage: "This field is required.",
        })
      : !isPrediction && showFeedback && feedback && hasValue
        ? intl.formatMessage({ id: feedback, defaultMessage: feedback })
        : undefined;

    const translatedHint = hint
      ? intl.formatMessage({ id: hint, defaultMessage: hint })
      : undefined;

    const shouldShowFeedback =
      !isPrediction && (isRequiredEmpty || (!!showFeedback && hasValue));

    // `shouldShowFeedback` is also true for a required-but-empty cell, but that
    // cell is incomplete, not wrong: it renders a warning (no check/cross) and
    // the control is already described as "This field is required.". Calling it
    // "Incorrect" would contradict the visual and double up the description.
    const feedbackStatus =
      shouldShowFeedback && !isRequiredEmpty
        ? intl.formatMessage(
            isCorrect ? messages.cellCorrect : messages.cellIncorrect
          )
        : undefined;

    return {
      hasValue,
      cellClass: getCellClass(),
      state: getState(),
      suffixIcon: getSuffixIcon(),
      translatedFeedback,
      translatedHint,
      shouldShowFeedback,
      feedbackStatus,
    };
  }, [
    value,
    showFeedback,
    isCorrect,
    feedback,
    hint,
    isPrediction,
    isIncomplete,
    intl,
  ]);
};
